import { useState } from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCategories } from "@/hooks/useCategories";

interface RecurringFiltersProps {
  filters: {
    type: string;
    patternType: string;
    status: string;
    category: string;
    dateRange: string;
  };
  onFiltersChange: (filters: any) => void;
}

const typeOptions = [
  { value: "all", label: "Todas" },
  { value: "income", label: "Receitas" },
  { value: "expense", label: "Despesas" }
];

const patternTypes = [
  { value: "all", label: "Todos os padrões" },
  { value: "daily", label: "Diário" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensal" },
  { value: "yearly", label: "Anual" }
];

const statusOptions = [
  { value: "all", label: "Todas" },
  { value: "active", label: "Ativas" },
  { value: "paused", label: "Pausadas" }
];

const dateRangeOptions = [
  { value: "all", label: "Todos os períodos" },
  { value: "this-month", label: "Este mês" },
  { value: "next-month", label: "Próximo mês" },
  { value: "this-quarter", label: "Este trimestre" },
  { value: "this-year", label: "Este ano" },
  { value: "next-year", label: "Próximo ano" }
];

export function RecurringFilters({
  filters,
  onFiltersChange
}: RecurringFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { categories, loading: categoriesLoading } = useCategories();

  const handleFilterChange = (key: string, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      type: "all",
      patternType: "all",
      status: "all",
      category: "all",
      dateRange: "all"
    });
  };

  const hasActiveFilters =
    filters.type !== "all" ||
    filters.patternType !== "all" ||
    filters.status !== "all" ||
    filters.category !== "all" ||
    filters.dateRange !== "all";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-muted-foreground">Filtros</Label>
          {hasActiveFilters && (
            <span className="text-xs text-primary">
              {[
                filters.type !== "all" && 1,
                filters.patternType !== "all" && 1,
                filters.status !== "all" && 1,
                filters.category !== "all" && 1,
                filters.dateRange !== "all" && 1
              ].filter(Boolean).length} ativo(s)
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="h-7 px-2"
        >
          <Filter className="h-4 w-4" />
          <span className="sr-only">Toggle filters</span>
        </Button>
      </div>

      {/* Filters Grid */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 ${!isExpanded ? "hidden lg:grid" : ""}`}>
        {/* Type Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Tipo</Label>
          <Select
            value={filters.type}
            onValueChange={(value) => handleFilterChange("type", value)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pattern Type Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Padrão</Label>
          <Select
            value={filters.patternType}
            onValueChange={(value) => handleFilterChange("patternType", value)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {patternTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Status Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select
            value={filters.status}
            onValueChange={(value) => handleFilterChange("status", value)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Category Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Categoria</Label>
          <Select
            value={filters.category}
            onValueChange={(value) => handleFilterChange("category", value)}
            disabled={categoriesLoading}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={categoriesLoading ? "..." : "Todas"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id.toString()} value={category.id.toString()}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Range Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Período</Label>
          <Select
            value={filters.dateRange}
            onValueChange={(value) => handleFilterChange("dateRange", value)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="lg:col-span-5 sm:col-span-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearFilters}
              className="w-full sm:w-auto h-8"
            >
              <X className="h-3.5 w-3.5 mr-1.5" />
              Limpar Filtros
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
